import { Link } from "react-router-dom";
import css from "./detail.module.css";

/**
 * The return row — R-1 to R-4, from the Turn 3 handoff.
 *
 * Opening a tab used to be a place with no exit: the nav still marked `TABS`
 * active, but nothing said where in Tabs you were or how to get out, and a
 * shared link had no history to go back to. This is the patron-side application
 * of the rule Studio already follows — the primary nav never moves, and depth
 * goes in a second row underneath it.
 *
 * Presentational. Everything it needs decided has been decided above it: it is
 * handed a game, a title and an optional position, and draws them. In
 * particular it does not know *why* there is no stepper — see R-D2, where that
 * is the difference between a deep link and an arrival.
 */

export type Stepper = {
  /** 1-based, for display. */
  position: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
};

export function ReturnRow({
  game,
  title,
  onBack,
  onGame,
  stepper,
}: {
  game?: { id: string; name: string };
  title: string;
  onBack: () => void;
  onGame: (gameId: string) => void;
  /** Absent on a deep-link arrival — R-10 hides it as one unit. */
  stepper?: Stepper;
}) {
  return (
    <div className={css.returnRow}>
      {/* R-NFR-2: a real breadcrumb, so the trail is navigable as a landmark. */}
      <nav aria-label="Breadcrumb" className={css.crumbCluster}>
        <ol className={css.crumbList}>
          <li className={css.crumbItem}>
            {/*
              R-5 / R-D3. An anchor with a real href, so middle-click,
              copy-link and open-in-new-tab all work — and a plain left click
              routed through `onBack`, which is a **navigation to /tabs**, never
              `history.back()`. Back-by-history works for everyone who arrived
              from the grid and dead-ends exactly the readers who arrive by
              shared link.

              A bevel button, not a text link: this is the 90% action on the
              screen and it gets a hit area.
            */}
            <Link
              to="/tabs"
              className={css.backButton}
              aria-label="Back to all tabs"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                onBack();
              }}
            >
              <span className={css.backChevron} aria-hidden="true">
                &#8249;
              </span>
              <span className={css.backLabel}>TABS</span>
            </Link>
          </li>

          {game && (
            <li className={css.crumbItem}>
              {/* Structure, not content — dimmer than either neighbour. */}
              <span className={css.crumbSep} aria-hidden="true">
                /
              </span>
              {/*
                R-8: back to the grid filtered to this game. The handoff calls
                it "cheap sideways depth" — it answers "what else is there from
                this game" with no new screen and no new route.
              */}
              <Link
                to={`/tabs?game=${encodeURIComponent(game.id)}`}
                className={css.crumbGame}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  onGame(game.id);
                }}
              >
                {game.name}
              </Link>
            </li>
          )}

          <li className={`${css.crumbItem} ${css.crumbTailItem}`}>
            <span className={css.crumbSep} aria-hidden="true">
              /
            </span>
            {/* Not a link: it is where you already are. */}
            <span className={css.crumbTail} aria-current="page">
              {title}
            </span>
          </li>
        </ol>
      </nav>

      {/*
        R-10: absent entirely on a deep link — label, arrows and count as one
        unit. There is no arrival list, and synthesising one from the unfiltered
        library would misrepresent where the reader came from.
      */}
      {stepper && (
        <div className={css.stepper}>
          {/* Names what the count counts, so `4 / 27` is not a mystery number. */}
          <span className={css.stepperLabel}>IN THIS LIBRARY</span>
          <div className={css.stepperArrows}>
            <button
              type="button"
              className={css.stepperArrow}
              aria-label="Previous tab"
              // R-11: disabled at the ends. No wrapping — a reader stepping off
              // the end and landing at the start has lost their place silently.
              disabled={stepper.position <= 1}
              onClick={stepper.onPrev}
            >
              &#8249;
            </button>
            {/* R-NFR-2: announced when it changes. */}
            <span className={css.stepperCount} aria-live="polite">
              {stepper.position} / {stepper.total}
            </span>
            <button
              type="button"
              className={css.stepperArrow}
              aria-label="Next tab"
              disabled={stepper.position >= stepper.total}
              onClick={stepper.onNext}
            >
              &#8250;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
